import { datadogLogs, LogsEvent } from '@datadog/browser-logs'
import {
  datadogRum,
  RumActionEventDomainContext,
  RumEvent,
} from '@datadog/browser-rum'

import { SearchMatchScoreDto } from '~shared/types/search.dto'

import {
  APP_ENV,
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
} from '~constants/config'
import { getBrowserInfo } from '~utils/user-agent'

const isMonitoredEnvironment = APP_ENV === 'production' || APP_ENV === 'staging'
const browserInfo = getBrowserInfo()

let initialized = false

export function startRumSessionMonitoring({
  browserId,
  sessionReplaySampleRate,
}: {
  browserId?: string
  sessionReplaySampleRate?: number
}) {
  if (
    !initialized &&
    isMonitoredEnvironment &&
    DATADOG_APPLICATION_ID &&
    DATADOG_CLIENT_TOKEN
  ) {
    datadogLogs.init({
      clientToken: DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'pair-search-frontend',
      env: APP_ENV,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      // Sample rate in % (i.e 100 refers to all sessions)
      sessionSampleRate: 100,
      forwardErrorsToLogs: true,
      beforeSend: (event: LogsEvent) => {
        if (event.http?.status_code === 0) {
          // Don't log XHR aborted requests
          return false
        }
        return true
      },
    })

    datadogRum.init({
      applicationId: DATADOG_APPLICATION_ID,
      clientToken: DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'pair-search-frontend',
      env: APP_ENV,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: sessionReplaySampleRate ?? 10,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,

      // See https://docs.datadoghq.com/real_user_monitoring/session_replay/privacy_options/#mask-mode
      defaultPrivacyLevel: 'allow',

      // Allows integration with DataDog Traces
      allowedTracingUrls: [/https:\/\/.*(\.)?pair\.gov\.sg/],

      beforeSend: (event: RumEvent, context) => {
        if (event.type === 'action' && event.action.type === 'click') {
          const actionContext = context as RumActionEventDomainContext
          // Make sure it's a link click
          const actionTarget = actionContext.events
            ? actionContext.events[0].target
            : null
          if (actionTarget && actionTarget instanceof HTMLElement) {
            const offsetParent = actionTarget.offsetParent
            if (offsetParent instanceof HTMLAnchorElement) {
              const rank =
                offsetParent?.attributes.getNamedItem('data-rank')?.value
              const matchScoreString =
                offsetParent?.attributes.getNamedItem('data-match-score')?.value
              const matchScore: SearchMatchScoreDto | null = matchScoreString
                ? JSON.parse(matchScoreString)
                : null
              const filterParamsString =
                offsetParent?.attributes.getNamedItem(
                  'data-filter-params',
                )?.value
              const filterParams: SearchMatchScoreDto | null =
                filterParamsString ? JSON.parse(filterParamsString) : null

              const searchId =
                offsetParent?.attributes.getNamedItem('data-search-id')?.value

              if (rank && matchScore && searchId) {
                const currentUrl = window.location.href
                const url = new URL(currentUrl)
                const queryParams = new URLSearchParams(url.search)
                const query = queryParams.get('query')

                event.context = {
                  ...event.context,
                  searchId,
                  rank: rank,
                  href: offsetParent.href,
                  query,
                  score: matchScore.score,
                  filters: filterParams,
                  ...matchScore.features,
                }
              }
            }
          }
        }
        return true
      },
    })

    // Note: no longer needed in datadogRum 5.0
    // datadogRum.startSessionReplayRecording()

    // Allows setting of user information on session metadata
    datadogRum.setUser({
      ...(!!browserId && { browser_id: browserId }),
      // TODO: Figure out how to cleanly override datadog's UA properties
      ...browserInfo,
    })

    initialized = true
  }
}

export function stopRumSessionMonitoring() {
  // Checks that datadogrum has been configured correctly before calling RUM
  if (datadogRum.getInitConfiguration()?.clientToken) {
    initialized = false
    return datadogRum.stopSessionReplayRecording()
  }
}
